import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Plus, Pencil, Trash2, Check, X, Eye, Play, History, Clock, ArrowUpDown, Search } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { sanitizeEmailHTML } from '@/utils/htmlSanitizer';

// Interface para os templates de email
interface EmailTemplate {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  is_active: boolean;
}

// Interface para os versions de templates
interface TemplateVersion {
  id: string;
  email_template_id: string;
  version_number: number;
  subject: string;
  html_content: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Adicionar interface para o preview
interface PreviewData {
  name: string;
  email: string;
  company: string;
  date: string;
  link: string;
  code: string;
  product: string;
  amount: string;
  invoice_number: string;
  [key: string]: string;
}

// Schema de validação para o formulário de template
const templateSchema = z.object({
  name: z.string().min(3, { message: 'O nome deve ter pelo menos 3 caracteres' }),
  is_active: z.boolean().default(true)
});

type TemplateFormValues = z.infer<typeof templateSchema>;

const EmailTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<EmailTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isVersionsDialogOpen, setIsVersionsDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<EmailTemplate | null>(null);
  const [currentVersion, setCurrentVersion] = useState<TemplateVersion | null>(null);
  const [activeVersion, setActiveVersion] = useState<TemplateVersion | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'updated_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Formulário para adicionar/editar templates
  const templateForm = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      is_active: true
    }
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Filter templates based on search query
    if (searchQuery.trim() === '') {
      setFilteredTemplates(templates);
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = templates.filter(template => 
        template.name.toLowerCase().includes(lowercasedQuery) || 
        (template.description && template.description.toLowerCase().includes(lowercasedQuery))
      );
      setFilteredTemplates(filtered);
    }
  }, [searchQuery, templates]);

  // Buscar templates de email
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (error) throw error;
      
      if (!import.meta.env.PROD) console.log('Templates fetched:', data);
      setTemplates(data || []);
      setFilteredTemplates(data || []);
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: 'name' | 'created_at' | 'updated_at') => {
    // If clicking the same column, toggle order; otherwise, set new column with asc order
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    // Re-fetch templates when sort parameters change
    fetchTemplates();
  }, [sortBy, sortOrder]);

  // Buscar versão ativa de um template
  const fetchActiveTemplateVersion = async (templateId: string) => {
    try {
      const { data, error } = await supabase
        .from('email_template_versions')
        .select('*')
        .eq('email_template_id', templateId)
        .eq('active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 é o código para "nenhum resultado" que não é realmente um erro aqui
        console.error('Erro ao buscar versão ativa:', error);
      }
      
      return data || null;
    } catch (error: any) {
      console.error('Erro ao buscar versão ativa:', error);
      return null;
    }
  };

  // Buscar versões de um template
  const fetchTemplateVersions = async (templateId: string): Promise<TemplateVersion[]> => {
    try {
      setVersionsLoading(true);
      const { data, error } = await supabase
        .from('email_template_versions')
        .select('*')
        .eq('email_template_id', templateId)
        .order('version_number', { ascending: false });

      if (error) throw error;
      
      if (!import.meta.env.PROD) console.log('Template versions fetched:', data);
      setVersions(data || []);

      // Log the active version for debugging
      const activeVersion = data?.find(v => v.active);
      if (!import.meta.env.PROD) console.log('Active version:', activeVersion);
      
      return data || [];
    } catch (error: any) {
      console.error('Error fetching template versions:', error);
      toast.error('Failed to load template versions', {
        description: error.message
      });
      return [];
    } finally {
      setVersionsLoading(false);
    }
  };

  // Abrir diálogo para adicionar template
  const openAddDialog = () => {
    templateForm.reset({
      name: '',
      is_active: true
    });
    setIsAddDialogOpen(true);
  };

  // Abrir diálogo para editar template
  const openEditDialog = (template: EmailTemplate) => {
    setCurrentTemplate(template);
    templateForm.reset({
      name: template.name,
      is_active: template.is_active
    });
    setIsEditDialogOpen(true);
  };

  // Abrir diálogo para visualizar template
  const openViewDialog = async (template: EmailTemplate) => {
    setCurrentTemplate(template);
    
    // Buscar a versão ativa para exibição
    const versionsData = await fetchTemplateVersions(template.id);
    const activeVersion = versionsData.find(v => v.active) || null;
    setCurrentVersion(activeVersion);
    
    setIsViewDialogOpen(true);
  };

  // Abrir diálogo para excluir template
  const openDeleteDialog = (template: EmailTemplate) => {
    setCurrentTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  // Abrir diálogo para visualizar versões do template
  const openVersionsDialog = (template: EmailTemplate) => {
    setCurrentTemplate(template);
    setVersions([]); // Reset versions for the new template
    setIsVersionsDialogOpen(true);
    fetchTemplateVersions(template.id);
  };

  // Abrir diálogo para preview do template
  const openPreviewDialog = async (template: EmailTemplate) => {
    try {
      setCurrentTemplate(template);
      
      // Buscar a versão ativa para preview
      const versionsData = await fetchTemplateVersions(template.id);
      const activeVersion = versionsData.find(v => v.active);
      
      if (!activeVersion) {
        toast.error('Este template não possui uma versão ativa para preview');
        return;
      }
      
      // Dados de exemplo para o preview
      const previewData: PreviewData = {
        name: 'João Silva',
        email: 'joao.silva@exemplo.com',
        company: 'Empresa ABC Ltda',
        date: new Date().toLocaleDateString('pt-BR'),
        link: 'https://exemplo.com/confirmar',
        code: '123456',
        product: 'Plano Premium',
        amount: 'R$ 99,90',
        invoice_number: 'INV-2023-001'
      };
      
      // Substituir variáveis no template
      let previewContent = activeVersion.html_content;
      
      // Substituir variáveis no formato {{variavel}}
      Object.keys(previewData).forEach(key => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        previewContent = previewContent.replace(regex, previewData[key]);
      });
      
      // Adicionar estilos básicos para o preview
      const styledHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
          ${previewContent}
        </div>
      `;
      
      setPreviewHtml(styledHtml);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      toast.error('Erro ao gerar preview do template');
    }
  };

  // Adicionar novo template (apenas nome)
  const handleAddTemplate = async (values: TemplateFormValues) => {
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .insert([values])
        .select();

      if (error) throw error;
      
      toast.success('Template de email criado com sucesso');
      setIsAddDialogOpen(false);
      
      if (data && data.length > 0) {
        // Vamos abrir o diálogo para adicionar uma versão
        navigate(`/templates/${data[0].id}/versions/new`);
      } else {
        fetchTemplates();
      }
    } catch (error: any) {
      console.error('Erro ao criar template:', error);
      toast.error('Erro ao criar template de email', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Editar template existente (apenas nome e status)
  const handleEditTemplate = async (values: TemplateFormValues) => {
    if (!currentTemplate) return;
    
    setSubmitting(true);
    try {
      // Atualizar o template principal
      const { error } = await supabase
        .from('email_templates')
        .update(values)
        .eq('id', currentTemplate.id);

      if (error) throw error;
      
      toast.success('Template de email atualizado com sucesso');
      setIsEditDialogOpen(false);
      fetchTemplates();
    } catch (error: any) {
      console.error('Erro ao atualizar template:', error);
      toast.error('Erro ao atualizar template de email', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Excluir template
  const handleDeleteTemplate = async () => {
    if (!currentTemplate) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', currentTemplate.id);

      if (error) throw error;
      
      toast.success('Template de email excluído com sucesso');
      setIsDeleteDialogOpen(false);
      fetchTemplates();
    } catch (error: any) {
      console.error('Erro ao excluir template:', error);
      toast.error('Erro ao excluir template de email', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Ativar uma versão específica
  const activateVersion = async (versionId: string, templateId: string) => {
    setSubmitting(true);
    try {
      // Desativar todas as versões
      const { error: deactivateError } = await supabase
        .from('email_template_versions')
        .update({ active: false })
        .eq('email_template_id', templateId);
        
      if (deactivateError) throw deactivateError;
      
      // Ativar a versão selecionada
      const { error } = await supabase
        .from('email_template_versions')
        .update({ active: true })
        .eq('id', versionId);

      if (error) throw error;
      
      // Atualizar a lista de versões
      await fetchTemplateVersions(templateId);
      
      toast.success('Versão do template ativada com sucesso');
    } catch (error: any) {
      console.error('Erro ao ativar versão:', error);
      toast.error('Erro ao ativar versão do template', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Formato da data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="w-full py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold">Templates de Email</h1>
          <p className="text-muted-foreground">
            Gerencie os templates para emails transacionais do sistema
          </p>
        </div>
        <Button onClick={openAddDialog} className="gap-1">
          <Plus className="h-4 w-4" /> Novo Template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> Templates de Email
          </CardTitle>
          <CardDescription>
            Lista de todos os templates de email disponíveis no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar templates..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Templates</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4">
              <p>Carregando templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-4">Nenhum template encontrado</p>
              <Button onClick={openAddDialog} variant="outline" className="gap-1">
                <Plus className="h-4 w-4" /> Criar seu primeiro template
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Nome
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Criado em
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center gap-1">
                      Atualizado em
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-extrabold">{template.name}</TableCell>
                    <TableCell>
                      {template.is_active ? (
                        <Badge variant="success">Ativo</Badge>
                      ) : (
                        <Badge variant="outline">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {template.created_at
                        ? format(new Date(template.created_at), 'dd/MM/yyyy', { locale: ptBR })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {template.updated_at
                        ? format(new Date(template.updated_at), 'dd/MM/yyyy', { locale: ptBR })
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(template)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Editar Template</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openVersionsDialog(template)}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Histórico de Versões</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/templates/${template.id}/versions/new`)}
                            >
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Adicionar Nova Versão</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para adicionar template (apenas nome) */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Template de Email</DialogTitle>
            <DialogDescription>
              Crie um novo template para uso em emails transacionais
            </DialogDescription>
          </DialogHeader>

          <Form {...templateForm}>
            <form onSubmit={templateForm.handleSubmit(handleAddTemplate)} className="space-y-6">
              <FormField
                control={templateForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Template</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Confirmação de Cadastro" {...field} />
                    </FormControl>
                    <FormDescription>
                      Um nome descritivo para identificar o template
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={templateForm.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Switch 
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer">Ativo</FormLabel>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Criando...' : 'Criar Template'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Diálogo para editar template (apenas nome) */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Template de Email</DialogTitle>
            <DialogDescription>
              Modifique as informações básicas do template
            </DialogDescription>
          </DialogHeader>

          <Form {...templateForm}>
            <form onSubmit={templateForm.handleSubmit(handleEditTemplate)} className="space-y-6">
              <FormField
                control={templateForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Template</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Confirmação de Cadastro" {...field} />
                    </FormControl>
                    <FormDescription>
                      Um nome descritivo para identificar o template
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={templateForm.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Switch 
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer">Ativo</FormLabel>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Diálogo para visualizar template */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Visualizar Template</DialogTitle>
            <DialogDescription>
              {currentTemplate?.name}
            </DialogDescription>
          </DialogHeader>

          {currentVersion ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-extrabold mb-1">Assunto:</h3>
                <p className="text-sm bg-muted p-2 rounded">{currentVersion.subject}</p>
              </div>

              <div>
                <h3 className="text-sm font-extrabold mb-1">Corpo do Email:</h3>
                <div className="bg-muted p-3 rounded overflow-auto max-h-96">
                  <pre className="text-sm whitespace-pre-wrap font-mono">{currentVersion.html_content}</pre>
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                <p>Versão: v{currentVersion.version_number}</p>
                <p>Criado em: {formatDate(currentVersion.created_at)}</p>
              </div>
            </div>
          ) : (
            <div className="py-6 text-center text-muted-foreground">
              Este template não possui uma versão ativa para visualizar.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para excluir template */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Template de Email</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este template? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <div className="py-3">
            <p className="font-extrabold">{currentTemplate?.name}</p>
            <p className="text-sm text-muted-foreground">
              Todas as versões associadas a este template também serão excluídas.
            </p>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteTemplate} 
              disabled={submitting}
            >
              {submitting ? 'Excluindo...' : 'Excluir Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para visualizar versões */}
      <Dialog open={isVersionsDialogOpen} onOpenChange={setIsVersionsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Histórico de Versões</DialogTitle>
            <DialogDescription>
              Histórico de versões do template "{currentTemplate?.name}"
            </DialogDescription>
          </DialogHeader>

          {currentTemplate && versions.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button 
                  size="sm" 
                  onClick={() => navigate(`/templates/${currentTemplate.id}/versions/new`)}
                  className="gap-1"
                >
                  <Plus className="h-3 w-3" /> Nova Versão
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Versão</TableHead>
                    <TableHead>Assunto</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Atualizado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell className="font-extrabold">v{version.version_number}</TableCell>
                      <TableCell>{version.subject}</TableCell>
                      <TableCell>
                        {version.active ? (
                          <Badge className="bg-green-500 hover:bg-green-600">Ativa</Badge>
                        ) : (
                          <Badge variant="outline">Inativa</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {version.created_at
                          ? format(new Date(version.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {version.updated_at
                          ? format(new Date(version.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={submitting}
                            onClick={() => navigate(`/templates/${currentTemplate.id}/versions/${version.id}/edit`)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={version.active || submitting}
                            onClick={() => activateVersion(version.id, currentTemplate.id)}
                          >
                            Ativar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Carregando versões...
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsVersionsDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para preview do template */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Preview do Template</DialogTitle>
            <DialogDescription>
              {currentTemplate?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="border rounded">
            <div className="border-b p-3 bg-muted/30">
              <div className="text-sm">
                <span className="font-extrabold">De:</span> Sistema &lt;sistema@exemplo.com&gt;
              </div>
              <div className="text-sm">
                <span className="font-extrabold">Para:</span> João Silva &lt;joao.silva@exemplo.com&gt;
              </div>
              <div className="text-sm">
                <span className="font-extrabold">Assunto:</span> {activeVersion?.subject}
              </div>
            </div>
            <div className="p-4">
              <div 
                className="email-preview" 
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHTML(previewHtml) }} 
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates; 